import './edit-pma.scss';
import Header from '../../../organisms/header/header.js';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import { Alert, InputBase, Snackbar } from '@mui/material';
import Grid from "@mui/material/Grid2";
import Input from "../../../atoms/input";
import apiClient from "../../../../utils/apiClient";
import { useForm } from 'react-hook-form';
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import FormControlLabel from "@mui/material/FormControlLabel";
import BackButton from '../../../atoms/back-button';

function PmaEditPma() {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;

  const navigate = useNavigate();
  const location = useLocation();
  const pmaId = location.state?.id || null;
  const { breadcrumbName } = location.state || {};

  const [countries, setCountries] = useState([]);
  const [countriesToManage, setCountriesToManage] = useState([]); // códigos: ["AT", "DE", ...]
  const [languages, setLanguages] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');

  // Teléfono general
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const [canNominate, setCanNominate] = useState(false);
  const handleNominateChange = (event) => {
    setCanNominate(event.target.checked); // Actualiza el estado con el valor del checkbox
  };

  // Teléfonos por país gestionado:  { [areaCode]: { value, error, touched } }
  const [managedPhones, setManagedPhones] = useState({});
  const isEmptyPhone = (v) => !v || v.replace(/\D/g, '').length === 0;

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Helpers para PhoneInput (ISO2)
  const normalizeAreaCode = (code) => (code === 'LUX' ? 'LU' : code);
  const toPhoneIso2 = (code) => (normalizeAreaCode(code || '').toLowerCase());

  // Joi schema
  const schema = Joi.object({
    name: Joi.string().required().messages({ 'string.empty': t('alert-name') }),
    last_name: Joi.string().required().messages({ 'string.empty': t('alert-last-name') }),
    email: Joi.string().trim().email({ tlds: { allow: false } }).required().messages({
      'string.email': t('alert-email-format'),
      'string.empty': t('alert-email'),
    }),
    prefix_phone: Joi.string().pattern(/^\+[0-9]{2,3}$/).required().messages({
      'string.pattern.base': t('alert-prefix'),
      'string.empty': t('alert-prefix'),
    }),
    phone: Joi.string().pattern(/^[0-9]{7,15}$/).required().messages({
      'string.pattern.base': t('alert-phone-format'),
      'string.empty': t('alert-phone'),
    }),
    area_code: Joi.string().required().messages({ 'string.empty': t('alert-country') }),
    preferred_language: Joi.string().required().messages({ 'string.empty': t('alert-language') }),
    role: Joi.string().required().messages({ 'string.empty': t('alert-role') }),
    // aquí solo validamos que haya al menos un país
    area_code_visibility: Joi.array().min(1).required().messages({
      'array.base': t('alert-country'),
      'array.min': t('alert-country'),
      'any.required': t('alert-country')
    }),
  });

  const { register, handleSubmit, formState, setValue, setFocus } = useForm({
    resolver: joiResolver(schema),
    mode: 'onChange',
  });
  const { errors } = formState;

  // Países
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiClient.get(`${process.env.REACT_APP_API_URL}/hac/countries`);
        const filteredCountries = response.data.filter(country => country.area_code !== 'EN');
        setCountries(filteredCountries);
      } catch (error) {
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    };
    fetchCountries();
  }, [t]);

  // Cargar datos del PMA
  useEffect(() => {
    const fetchPmaData = async () => {
      try {
        const response = await apiClient.get(
          `${process.env.REACT_APP_API_URL}/pma?pmaId=${pmaId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
          const pmaData = response.data;

          // Campos básicos
          setCanNominate(pmaData.is_nominator);
          setValue('name', pmaData.name || '');
          setValue('last_name', pmaData.last_name || '');
          setValue('email', pmaData.email || '');
          setValue('prefix_phone', pmaData.prefix_phone || '');
          setValue('phone', pmaData.phone || '');
          setValue('area_code', pmaData.area_code || '');
          setValue('role', pmaData.role || '');
          setPhone(`${pmaData.prefix_phone || ''}${pmaData.phone || ''}`);

          // Idiomas del país
          const country = countries.find((c) => c.area_code === pmaData.area_code);
          if (country && country.language) {
            setLanguages(country.language);
            setTimeout(() => {
              setValue('preferred_language', pmaData.preferred_language || '');
            }, 0);
          } else {
            setLanguages([]);
            setValue('preferred_language', '');
          }

          // Gestión de países y teléfonos específicos
          const visibility = pmaData.area_code_visibility || [];
          // Puede venir como array de objetos (nuevo) o de strings (viejo) → soportamos ambos
          let codes = [];
          const initialManagedPhones = {};

          if (visibility.length && typeof visibility[0] === 'string') {
            // Versión antigua: ["AT", "DE", ...]
            codes = visibility;
          } else {
            // Versión nueva: [{ area_code, prefix_phone, phone }]
            codes = visibility.map(v => v.area_code);
            visibility.forEach(v => {
              const code = v.area_code;
              initialManagedPhones[code] = {
                value: `${v.prefix_phone || ''}${v.phone || ''}`,
                error: '',
                touched: false,
              };
            });
          }

          setCountriesToManage(codes);
          setValue('area_code_visibility', codes); // para Joi (validamos solo que haya países)
          setManagedPhones(initialManagedPhones);
        }
      } catch (error) {
        console.error('Error fetching PMA data:', error);
        setSnackbarMessage(t('common-internal-server-error'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    if (pmaId && countries.length) {
      fetchPmaData();
    }
  }, [pmaId, token, setValue, t, countries]);

  // Cambio de país del usuario (no los gestionados)
  const handleAreaCodeChange = (event) => {
    const { value } = event.target;
    setValue('area_code', value);

    const selectedCountry = countries.find(country => country.area_code === value);
    if (selectedCountry) {
      setLanguages(selectedCountry.language || []);
    } else {
      setLanguages([]);
    }
  };

  // Cambio de países gestionados (multi-select)
  const handleManagedAreaCodeChange = (event) => {
    const { value } = event.target;
    const uniqueValues = Array.isArray(value) ? [...new Set(value)] : [value];

    setCountriesToManage(uniqueValues);
    setValue('area_code_visibility', uniqueValues); // solo para la validación de Joi

    // Asegurar que tenemos estado de teléfono para cada país seleccionado
    setManagedPhones((prev) => {
      const next = { ...prev };

      // añadir nuevos
      uniqueValues.forEach((code) => {
        if (!next[code]) {
          next[code] = { value: '', error: '', touched: false };
        }
      });

      // eliminar los que ya no están
      Object.keys(next).forEach((code) => {
        if (!uniqueValues.includes(code)) {
          delete next[code];
        }
      });

      return next;
    });
  };

  // Submit
  const onSubmit = async (data) => {
    // Validar teléfono general (PhoneInput de arriba)
    const parsedPhone = parsePhoneNumberFromString(phone);

    if (!parsedPhone || !parsedPhone.isValid()) {
      setPhoneError(t('alert-phone-format'));
      setFocus('phone');
      return;
    }
    setPhoneError('');

    const generalPrefix = `+${parsedPhone.countryCallingCode}`;
    const generalNumber = parsedPhone.nationalNumber?.toString() || '';

    // Construir area_code_visibility: si un teléfono específico está vacío o inválido,
    // usar el teléfono general
    const area_code_visibility = (countriesToManage || []).map((code) => {
      const state = managedPhones[code] || { value: '' };
      let prefix_phone = '';
      let phoneNumber = '';

      if (!isEmptyPhone(state.value)) {
        const parsed = parsePhoneNumberFromString(state.value);
        if (parsed && parsed.isValid() && parsed.nationalNumber) {
          prefix_phone = `+${parsed.countryCallingCode}`;
          phoneNumber = parsed.nationalNumber.toString();
        }
      }

      if (!prefix_phone || !phoneNumber) {
        // vacío o inválido → usamos el general
        prefix_phone = generalPrefix;
        phoneNumber = generalNumber;
      }

      return {
        area_code: code,
        prefix_phone,
        phone: phoneNumber,
      };
    });

    const payload = {
      email: data.email,
      name: data.name,
      last_name: data.last_name,
      area_code: data.area_code,
      preferred_language: data.preferred_language,
      prefix_phone: generalPrefix,
      phone: generalNumber,
      area_code_visibility,
      is_nominator: canNominate
    };

    const url = `${process.env.REACT_APP_API_URL}/pma/${pmaId}`;

    try {
      const response = await apiClient.put(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200 || response.status === 201) {
        setSnackbarMessage(t('alert-data-saved'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => navigate("/pma/main/pma"), 2000);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setSnackbarMessage(t('alert-duplicate'));
        setSnackbarSeverity('error');
      } else {
        setSnackbarMessage(t('common-internal-server-error'));
        setSnackbarSeverity('error');
      }
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      {token && role === 'pmaGM' ? (
        <div className="App">
          <Header/>
          <main className="new-pma">
            <section className="form">
              <div className="inner">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <BackButton fallbackTo="/pma/main/pma" context={breadcrumbName} />
                  <h2>{t('admin-new-pma')}</h2>
                  <div>
                    <p><strong>{t('common-basic-info')}</strong></p>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="name"
                            label={t('common-name')}
                            error={errors?.name}
                            {...register('name')}
                          />
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="last_name"
                            label={t('common-last-name')}
                            error={errors?.last_name}
                            {...register('last_name')}
                          />
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="email"
                            readOnly
                            label={t('common-email')}
                            error={errors?.email}
                            {...register('email')}
                          />
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                        <div className="form-control required">
                          <label htmlFor="phone">{t('common-phone')}</label>
                          <PhoneInput
                            value={phone}
                            onChange={(value) => {
                              setPhone(value);
                              setPhoneError('');
                            }}
                            onBlur={() => {
                              const parsed = parsePhoneNumberFromString(phone);
                              if (!parsed || !parsed.isValid()) {
                                setPhoneError(t('alert-phone-format'));
                              } else {
                                setPhoneError('');
                              }
                            }}
                          />
                          {phoneError && <p className="error-message">{phoneError}</p>}
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 1, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="area_code">{t('common-country')}</label>
                          <select
                            name="area_code"
                            id="area_code"
                            className="normal"
                            {...register('area_code')}
                            onChange={handleAreaCodeChange}
                          >
                            <option value="">{t('common-select-country')}</option>
                            {countries.map(country => (
                              <option key={country.area_code} value={country.area_code}>
                                {t(`countries-${country.area_code}`)}
                              </option>
                            ))}
                          </select>
                          {errors.area_code && (
                            <span className="input__error">{errors.area_code.message}</span>
                          )}
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 1, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="preferred_language">{t('common-select-language')}</label>
                          <select
                            name="preferred_language"
                            id="preferred_language"
                            className="normal"
                            {...register('preferred_language')}
                          >
                            <option value="">{t('common-select-language')}</option>
                            {languages.map((language) => (
                              <option key={language} value={language}>
                                {t(`languages-${language}`)}
                              </option>
                            ))}
                          </select>
                          {errors.preferred_language && (
                            <span className="input__error">{errors.preferred_language.message}</span>
                          )}
                        </div>
                      </Grid>

                    </Grid>
                    <br/><br/>
                    <p><strong>{t('common-settings-pma')}</strong></p>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="role">{t('common-admin-role')}</label>
                          <select
                            name="role"
                            id="role"
                            className="normal"
                            disabled
                            {...register('role')}
                          >
                            <option value="pmaGM">{t('common-role-pmaGM')}</option>
                            <option value="pma">{t('common-role-pma')}</option>
                          </select>
                          {errors.role && (
                            <span className="input__error">{errors.role.message}</span>
                          )}
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="area_code_visibility">{t('common-manage-country')}</label>
                          <FormControl fullWidth>
                            <Select
                              id="area_code_visibility"
                              className="multiselect"
                              multiple
                              value={countriesToManage}
                              onChange={handleManagedAreaCodeChange}
                              input={<InputBase name="area_code_visibility" id="area_code_visibility" />}
                              renderValue={(selected) => selected.join(', ')}
                            >
                              {countries.map((country) => (
                                <MenuItem
                                  key={country.area_code}
                                  value={country.area_code}
                                  className="multiselect-options"
                                >
                                  <Checkbox checked={countriesToManage.includes(country.area_code)} />
                                  <ListItemText primary={t(`countries-${country.area_code}`)} />
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.area_code_visibility && (
                              <span className="input__error">{errors.area_code_visibility.message}</span>
                            )}
                          </FormControl>
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={canNominate}
                              onChange={handleNominateChange}
                            />
                          }
                          label={t('admin-new-admin-nominate')}
                        />
                      </Grid>
                    </Grid>

                    {/* Teléfonos por país gestionado */}
                    <Grid container columnSpacing={3} rowSpacing={0}>
                      {countriesToManage.map((code) => {
                        const iso2Lower = toPhoneIso2(code);
                        const labelCountry = t(`countries-${code}`);
                        const state = managedPhones[code] || { value: '', error: '', touched: false };

                        return (
                          <Grid key={code} size={{ xs: 12, sm: 12, md: 6 }}>
                            <div className="form-control">
                              <label>{t('common-phone')} — {labelCountry}</label>
                              <PhoneInput
                                defaultCountry={iso2Lower}
                                value={state.value}
                                onChange={(val) => {
                                  setManagedPhones((prev) => {
                                    const next = {
                                      ...prev,
                                      [code]: { ...state, value: val }
                                    };
                                    // si ya fue tocado, validamos formato (vacío permitido: se rellena con el general)
                                    if (state.touched && !isEmptyPhone(val)) {
                                      const parsed = parsePhoneNumberFromString(val);
                                      if (!parsed || !parsed.isValid() || !parsed.nationalNumber) {
                                        next[code].error = t('alert-phone-format');
                                      } else {
                                        next[code].error = '';
                                      }
                                    }
                                    if (state.touched && isEmptyPhone(val)) {
                                      next[code].error = '';
                                    }
                                    return next;
                                  });
                                }}
                                onBlur={() => {
                                  setManagedPhones((prev) => {
                                    const next = {
                                      ...prev,
                                      [code]: { ...state, touched: true }
                                    };
                                    if (isEmptyPhone(state.value)) {
                                      next[code].error = '';
                                      return next;
                                    }
                                    const parsed = parsePhoneNumberFromString(state.value);
                                    if (!parsed || !parsed.isValid() || !parsed.nationalNumber) {
                                      next[code].error = t('alert-phone-format');
                                    } else {
                                      next[code].error = '';
                                    }
                                    return next;
                                  });
                                }}
                              />
                              {state.error && <span className="error-message">{state.error}</span>}
                            </div>
                          </Grid>
                        );
                      })}
                    </Grid>

                    <Grid container spacing={3} >
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <button className="btn" type="submit">
                          {t('common-save')}
                        </button>
                      </Grid>
                    </Grid>
                  </div>
                </form>
              </div>
            </section>
          </main>
          <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
            <Alert severity={snackbarSeverity} sx={{width: '100%'}}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <Navigate to="/login"/>
      )}
    </>
  );
}

export default PmaEditPma;
